import React, { useMemo, useState } from 'react'
import { Alert, Button, Flex, Statistic, Tag, Tooltip, Typography } from 'antd'
import { GridOptions } from 'ag-grid-community'
import { EyeOutlined, UnorderedListOutlined, WarningOutlined } from '@ant-design/icons'
import { CustomAgGrid } from '@/common/components/CustomAgGrid.tsx'
import { useGetAllEmployeeDataAccessClaimsQuery } from '@/features/portal-configuration/portal-configuration.api.ts'
import { UserEmployeeDataAccessSummary } from '@/features/portal-configuration/employee-data-access-claim.def.ts'
import {
  EmployeeDataAccessClaimDrawer,
} from '@/pages/portal-configuration/components/EmployeeDataAccessClaimDrawer.tsx'
import { SectionGroupLegend, SectionGroupTags } from '@/pages/portal-configuration/components/SectionGroupTags.tsx'
import { SectionReferenceDrawer } from '@/pages/portal-configuration/components/SectionReferenceDrawer.tsx'
import { uniq } from 'lodash-es'

/**
 * Every section the user holds anywhere, in either set. The response carries the sensitive union
 * per user but not the full one, so the grouped slugs need it derived here.
 */
const sectionsHeldByUser = (user: UserEmployeeDataAccessSummary): string[] =>
  uniq(user.entities.flatMap(entity => [...entity.generalSections, ...entity.hcSections]))

/**
 * Read-only inventory of every user's entity-scoped employee data access claim.
 *
 * The grid is deliberately one row per user with the detail behind a drawer: a user can hold many
 * entities, each with two section sets, so the flattened form would be unreadable. The three things
 * worth seeing at a glance are how many entities a user reaches, whether they can reach Human
 * Capital employees at all, and whether any sensitive section is involved.
 */
export const EmployeeDataAccessClaimTab: React.FC = () => {
  const { data, refetch, isFetching } = useGetAllEmployeeDataAccessClaimsQuery()
  const [targetUser, setTargetUser] = useState<UserEmployeeDataAccessSummary>()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [sectionReferenceOpen, setSectionReferenceOpen] = useState(false)

  const columnDefs: GridOptions<UserEmployeeDataAccessSummary>['columnDefs'] = useMemo(() => [
    {
      headerName: 'Username',
      field: 'username',
      colId: 'username',
      width: 160,
      pinned: 'left',
    },
    {
      headerName: 'Entities',
      field: 'entityCount',
      colId: 'entityCount',
      width: 110,
      cellRenderer: ({ data: row }: { data?: UserEmployeeDataAccessSummary }) => {
        if (!row) return null
        // No entity means the claim grants nothing; such a user still reaches their own record and
        // their direct reports through paths that never consult the claim.
        return row.hasAnyAccess
          ? <span>{row.entityCount}</span>
          : <Tag className="m-0">No access</Tag>
      },
    },
    {
      headerName: 'Countries',
      colId: 'countries',
      width: 200,
      // Tags wrap, so the row grows to fit instead of clipping them. RowAutoHeightModule is
      // already registered in ag-grid-init.ts.
      autoHeight: true,
      wrapText: true,
      valueGetter: ({ data: row }) => row?.countries.join(', ') ?? '',
      cellRenderer: ({ data: row }: { data?: UserEmployeeDataAccessSummary }) => {
        if (!row?.countries.length) return null
        // No h-full here: with autoHeight the content defines the row height, so stretching to the
        // cell would collapse it to a single line.
        return (
          <Flex gap={4} wrap="wrap" className="py-2">
            {row.countries.map(country => <Tag key={country} className="m-0">{country}</Tag>)}
          </Flex>
        )
      },
    },
    {
      headerName: 'Scope',
      colId: 'scope',
      width: 150,
      valueGetter: ({ data: row }) => (row?.hasHcAccess ? 'General + HC' : 'General only'),
      cellRenderer: ({ data: row }: { data?: UserEmployeeDataAccessSummary }) => {
        if (!row?.hasAnyAccess) return null
        return (
          <Flex gap={4} align="center" className="h-full">
            <Tag color="blue" className="m-0">General</Tag>
            {row.hasHcAccess
              ? (
                <Tooltip title="Can reach Human Capital department employees' data.">
                  <Tag color="purple" className="m-0">HC</Tag>
                </Tooltip>
              )
              : null}
          </Flex>
        )
      },
    },
    {
      headerName: 'Sections',
      colId: 'sections',
      flex: 1,
      minWidth: 220,
      autoHeight: true,
      wrapText: true,
      // The display is grouped, but the value stays the full readable section list so the column's
      // name matches what filtering and Excel export actually operate on. Sorted so the exported
      // cell is stable between calls.
      valueGetter: ({ data: row }) => (row ? sectionsHeldByUser(row).sort().join(', ') : ''),
      cellRenderer: ({ data: row }: { data?: UserEmployeeDataAccessSummary }) => {
        if (!row) return null
        if (!row.hasAnyAccess) return null
        return (
          <Flex className="py-2">
            <SectionGroupTags
              sections={sectionsHeldByUser(row)}
              sensitiveSections={data?.sensitiveSections || []}
            />
          </Flex>
        )
      },
    },
    {
      headerName: 'Actions',
      colId: 'actions',
      width: 70,
      filter: false,
      sortable: false,
      cellRenderer: ({ data: row }: { data?: UserEmployeeDataAccessSummary }) => {
        if (!row) return null
        return (
          <Flex align="center" className="h-full">
            <Tooltip title="View per-entity breakdown">
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                disabled={!row.hasAnyAccess}
                onClick={() => {
                  setTargetUser(row)
                  setDrawerOpen(true)
                }}
              >
                Details
              </Button>
            </Tooltip>
          </Flex>
        )
      },
    },
  ], [data?.sensitiveSections])

  return (
    <Flex vertical gap="middle">
      <Alert
        type="info"
        showIcon
        message="Employee data access claims are owned by the auth service"
        description={(
          <Flex vertical gap="small">
            <Typography.Text>
              This view is read-only. Grants are issued by the auth service and consumed by the
              portal, so changes have to be made there.
            </Typography.Text>
            <Typography.Text type="secondary">
              A section applies to an employee based on their department: Human Capital employees
              resolve against the <b>HC</b> set, everyone else against the <b>General</b> set.
            </Typography.Text>
            <SectionGroupLegend />
          </Flex>
        )}
      />

      <Flex gap="large" wrap="wrap">
        <Statistic title="Users" value={data?.userCount ?? 0} />
        <Statistic
          title={(
            <Flex gap={4} align="center">
              <WarningOutlined />
              <span>With sensitive access</span>
            </Flex>
          )}
          value={data?.usersWithSensitiveAccess ?? 0}
          valueStyle={{ color: (data?.usersWithSensitiveAccess ?? 0) > 0 ? '#cf1322' : undefined }}
        />
      </Flex>

      <CustomAgGrid<UserEmployeeDataAccessSummary>
        rowData={data?.users ?? []}
        columnDefs={columnDefs}
        refetch={refetch}
        isLoading={isFetching}
        allowExport
        gridOptions={{
          rowSelection: undefined,
        }}
        toolbarExtra={(
          <Button icon={<UnorderedListOutlined />} onClick={() => setSectionReferenceOpen(true)}>
            Section Reference
          </Button>
        )}
      />

      <EmployeeDataAccessClaimDrawer
        key={targetUser?.username}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false)
          setTargetUser(undefined)
        }}
        user={targetUser}
        sensitiveSections={data?.sensitiveSections || []}
      />

      <SectionReferenceDrawer
        open={sectionReferenceOpen}
        onClose={() => setSectionReferenceOpen(false)}
        sensitiveSections={data?.sensitiveSections || []}
      />
    </Flex>
  )
}
